/*
 * Раскладка пакетного режима — макет deliverables/ui-design-v1/build/bodies/Batch.html.
 *
 * Всё, что уже есть в глобальном base.css (.card-flat, .vrow, .val, .pill, .hold, .notice,
 * .meta, .micro, .mono, .num), здесь НЕ переопределяется: соответствие макету проверяется
 * сверкой классов, а не чтением двух файлов сразу. Тут только то, чего в статичном макете
 * быть не могло: скролл, курсор, состояния настоящих кнопок и клип компактного превью.
 *
 * Ни одного фирменного цвета литералом — только var(--*) из tokens.css. Единственное
 * вычисление цвета — color-mix поверх токена: макет рисует у заблокированного поста мягкую
 * красную рамку, а токена именно этой светлоты в шкале нет, и заводить его ради одной рамки
 * значит расширять палитру мимо брендбука.
 */

.page {
  /* Дашборд не растягивается во всю ширину монитора: строка таблицы на 1900px заставляет
     глаз ехать от края до края, а связь «подпись — значение» теряется. Предел один на три
     экрана, поэтому живёт токеном в tokens.css, а не тремя числами врозь. Экран черновика
     этот предел НЕ делит — у него свой (`--workspace-max`) и своё обоснование.
     Поля тоже токенами: на телефоне 32px с каждой стороны съедают шестую часть экрана. */
  width: 100%;
  max-width: var(--surface-max);
  margin-inline: auto;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: var(--page-pad-y) var(--page-pad-x);
  overflow-y: auto;
}

.head {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}

/*
 * Две колонки как в макете: карточки слева, слоты и утверждение справа. На узком окне
 * колонка 480px не помещается рядом с превью шириной сообщения — тогда одна колонка.
 */
.grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 480px);
  gap: 24px;
  align-items: start;
}
@media (max-width: 1280px) {
  .grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  min-width: 0;
}

/* Пост, который останавливает весь пакет, виден в списке, а не только в сводке справа. */
.cardBlocked {
  border-color: color-mix(in srgb, var(--red) 35%, var(--white));
}

.check {
  width: 18px;
  height: 18px;
  margin-top: 3px;
  flex: none;
  accent-color: var(--violet);
  cursor: pointer;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.titleRow {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}

.title {
  font-size: 16px;
}

/* Порядок карточек и есть порядок раздачи слотов, поэтому его двигают кнопками. */
.moves {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: none;
}

.move {
  width: 22px;
  height: 20px;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--white);
  color: var(--ink-2);
  font-family: var(--sans);
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
}
.move:hover:not(:disabled) {
  border-color: var(--violet-bg-2);
  background: var(--violet-bg);
}
.move:disabled {
  color: var(--muted);
  background: var(--bg-alt);
  cursor: default;
}

/*
 * Компактное превью — тот же MessagePreview, только подрезанный по высоте. Именно клип, а не
 * «краткий рендер»: второго рендера поста в приложении быть не должно, иначе человек в
 * пакете и человек на экране черновика смотрят на два разных текста.
 */
.preview {
  position: relative;
  max-height: 132px;
  overflow: hidden;
  border-radius: 12px;
}
.preview::after {
  content: "";
  position: absolute;
  inset: auto 0 0 0;
  height: 46px;
  background: linear-gradient(to bottom, transparent, var(--bg-alt));
  pointer-events: none;
}
.previewOpen {
  border-radius: 12px;
}

.footRow {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.mark {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
}
.markOk {
  color: var(--green-ink);
}
.markWarn {
  color: var(--amber-ink);
}
.markErr {
  color: var(--red-ink);
}

/* Чип визуала в шапке карточки. Тон предупреждения — только у «не решено»: доля постов с
   картинкой видна глазом на недельном горизонте, и красить в тревогу нормальный исход
   значило бы научить не замечать тревогу. */
.visualChip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--bg-alt);
  border: 1px solid var(--border);
  color: var(--ink-2);
  border-radius: var(--r-chip);
  font-size: 12px;
  font-weight: 500;
  padding: 3px 10px;
  white-space: nowrap;
}
.visualChipOpen {
  background: var(--amber-bg);
  border-color: var(--amber-bg);
  color: var(--amber-ink);
}

.slotTag {
  font-size: 12.5px;
  color: var(--accent-ink);
}
.slotNone {
  font-size: 12.5px;
  color: var(--muted);
}

.linkBtn {
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  font-family: var(--sans);
  font-size: 12.5px;
  color: var(--accent-ink);
  cursor: pointer;
}
.linkBtn:hover {
  color: var(--accent-ink-strong);
}

/* Правая колонка едет вместе со списком: карточек может быть двадцать. */
.side {
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: sticky;
  top: 0;
  min-width: 0;
}

.sideCard {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.queueSlot {
  width: 124px;
  flex: none;
  font-size: 12px;
}
.queueSlotNone {
  color: var(--red-ink);
}

.queueTitle {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.queueTitleNone {
  color: var(--muted);
}

.resultRow {
  display: flex;
  gap: 10px;
  align-items: baseline;
  min-width: 0;
}
.resultBody {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.state {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 0;
}

/*
 * Телефон (640): ячейка слота в очереди отпускает свои 124px. Фиксированная ширина строит
 * колонку времени, читаемую сверху вниз; на 390px она отбирала треть строки у заголовка
 * поста, и колонкой становилось многоточие. Читать очередь на телефоне всё равно приходится
 * построчно — колонка там не окупается.
 */
@media (max-width: 640px) {
  .queueSlot {
    width: auto;
    min-width: 0;
  }
}
